import React from "react"
import { graphql } from "gatsby"
import styled from "styled-components"
import { GatsbyImage, getImage } from "gatsby-plugin-image"
import Layout from "../components/layout"
import BlocksRenderer from "../components/blocks-renderer"
import Seo from "../components/seo"

const Article = styled.article`
  & {
    max-width: var(--width-reading, 700px);
    font-weight: 300;
    margin: 0 auto;
    padding: 0;
    @media (max-width: 800px) {
      padding: 0 1rem;
    }
  }

  h1 {
    margin-top: var(--spacing-xxl);
  }

  h2 {
    margin-top: var(--spacing-xl);
    margin-bottom: var(--spacing-m);
  }

  h3 {
    margin-top: var(--spacing-l);
  }

  blockquote {
    border-left: 2px solid var(--color-text);
    margin: 1.5em 10px;
    padding: var(--spacing-s) var(--spacing-m);
  }
`

const Header = styled.header`
  margin-top: var(--spacing-xxl);
`

const Title = styled.h1`
  font-weight: 800;
  font-size: var(--font-xxl);
`

const Description = styled.p`
  font-size: var(--font-l);
  color: var(--color-text-inactive);
  line-height: var(--line-height-breathable);
  font-weight: 300;
`

const Details = styled.div`
  display: grid;
  grid-template:
    "avatar author" min-content
    "avatar data" min-content
    / min-content 1fr;
  grid-gap: var(--spacing-xs) var(--spacing-m);
  padding-top: var(--spacing-s);
  padding-bottom: var(--spacing-l);
  border-bottom: var(--border);
  margin-bottom: var(--spacing-xxl);
`
const Avatar = styled(GatsbyImage)`
  grid-area: avatar;
  height: var(--spacing-xxl);
  width: var(--spacing-xxl);
  border-radius: var(--spacing-xxl);
`

const Author = styled.span`
  grid-area: author;
  align-self: flex-end;
`

const Data = styled.span`
  grid-area: data;
  font-size: var(--font-sm);
  color: var(--color-text-inactive);
`

const EndingSeparator = styled.div`
  text-align: center;
  font-size: var(--font-ml);
  letter-spacing: var(--spacing-l);
  margin-top: var(--spacing-xxl);
  margin-bottom: 1em;
`

const Footer = styled.footer`
  max-width: var(--width-reading, 700px);
  width: 100%;
  margin: 0 auto;
`

const ArticlePage = ({ data }) => {
  const {
    strapiArticle: article,
    allStrapiEndingContentTextnode: allEndings,
    strapiEndingContentTextnode,
  } = data

  //Basically I take ending id and loop through all endings and get dynamically using id as array's order element (minus 1 ofcourse),
  // otherwise I take a default "Get in touch" ending,
  // maybe it's a temporary solution, maybe not)
  const articleEndingData = article?.ending?.strapi_id
    ? allEndings?.nodes?.[article?.ending?.strapi_id - 1]?.childMarkdownRemark
        ?.html
    : strapiEndingContentTextnode?.childMarkdownRemark?.html

  if (!article?.author || !article?.date) {
    throw new Error(
      `Missing one or more required article data! article: ${article}, article-author: ${article.author}, article-date: ${article.date}`
    )
  }

  const seo = {
    metaTitle: article.title,
    metaDescription: article.description,
    shareImage: article.cover,
  }

  return (
    <Layout as="article">
      <Seo seo={seo} />
      <Article>
        <Header>
          <Title>{article.title}</Title>
          <Description>{article.description}</Description>
          <Details>
            <Avatar
              image={getImage(article.author.avatar?.localFile)}
              alt={`A picture of ${article.author.name}`}
            />
            <Author>{article.author.name}</Author>
            <Data>
              <time>{article.date}</time>
            </Data>
          </Details>
        </Header>
        <BlocksRenderer blocks={article.blocks || []} />
        <EndingSeparator>···</EndingSeparator>
        <Footer
          dangerouslySetInnerHTML={{
            __html: articleEndingData,
          }}
        ></Footer>
      </Article>
    </Layout>
  )
}

export const pageQuery = graphql`
  query ($slug: String) {
    allStrapiEndingContentTextnode {
      nodes {
        childMarkdownRemark {
          html
        }
      }
    }
    strapiEndingContentTextnode {
      childMarkdownRemark {
        html
      }
    }
    strapiArticle(slug: { eq: $slug }) {
      id
      slug
      title
      description
      date(formatString: "MMMM D, YYYY")
      author {
        ...Author
      }
      blocks {
        ...Blocks
      }
      cover {
        alternativeText
        localFile {
          url
          childImageSharp {
            gatsbyImageData
          }
        }
      }
      ending {
        strapi_id
      }
    }
  }
`

export default ArticlePage
